import {
  Checkbox,
  DefaultButton,
  Dialog,
  DialogContent,
  DialogType,
  Dropdown,
  IDropdownOption,
  Label,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  TextField,
} from "@fluentui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteConfigurationSettings,
  getConfigurationListSettings,
  getEffectiveIndexation,
  getEffectiveValueChangeThresholds,
  getEffectiveValueFieldsVisibility,
  setConfigurationSettings,
  updateConfigurationSettings,
} from "../../../api/settingConfigurationApi";
import { toast } from "../../../components/FluentToast";
import { useSelector } from "../../../store/hooks";
import { EConfigurationSetting } from "../../../types/types";
import {
  getEffectiveThreshold,
  IValueChangeThresholdInfo,
  THRESHOLD_VALUE_FIELDS,
} from "../../../utils/valueChangeThresholds";
import {
  INDEXABLE_VALUE_FIELDS,
  IValueIndexationInfo,
} from "../../../utils/valueIndexation";
import {
  IValueFieldsVisibilityInfo,
  TOGGLEABLE_VALUE_FIELDS,
  toFormFieldName,
} from "../../../utils/valueFieldsVisibility";

const CLIENT_LEVEL_KEY = "client";

type Props = {
  showDialog: boolean;
  toggleShowDialog: () => void;
};

function ConfigValueFieldsDialog({ toggleShowDialog, showDialog }: Props) {
  const { t } = useTranslation();
  const client = useSelector((s) => s.clients.client);
  const clientId = client?.clientId;
  const locations = useSelector((s) => s.locations.locations);
  const campaignInfo = useSelector((s) => s.campaign.campaignInfo);
  const campaignIsClosed = campaignInfo?.isCampaignClosed || !campaignInfo;

  const [dataLoading, setDataLoading] = useState(false);
  const [scopeKey, setScopeKey] = useState<string>(CLIENT_LEVEL_KEY);
  // all ValueFieldsVisibility + ValueChangeThreshold rows of this client (client-level + location-level overrides)
  const [settingRows, setSettingRows] = useState<any[]>([]);
  const [effective, setEffective] =
    useState<IValueFieldsVisibilityInfo | null>(null);
  const [enabledFields, setEnabledFields] = useState<string[]>([
    ...TOGGLEABLE_VALUE_FIELDS,
  ]);
  const [effectiveThresholds, setEffectiveThresholds] =
    useState<IValueChangeThresholdInfo | null>(null);
  // threshold inputs kept as strings for editing; validated on save
  const [thresholdInputs, setThresholdInputs] = useState<
    Record<string, string>
  >({});
  const [effectiveIndexation, setEffectiveIndexation] =
    useState<IValueIndexationInfo | null>(null);
  const [indexationEnabled, setIndexationEnabled] = useState<string[]>([]);
  // indexation percentage inputs kept as strings for editing; validated on save
  const [indexationInputs, setIndexationInputs] = useState<
    Record<string, string>
  >({});

  const selectedLocationId =
    scopeKey === CLIENT_LEVEL_KEY ? null : Number(scopeKey);

  const rowMatchesScope = useCallback(
    (r: any) =>
      selectedLocationId === null
        ? !r.locationId
        : r.locationId === selectedLocationId,
    [selectedLocationId]
  );

  // the explicit override rows for the selected scope (if any)
  const scopeRow = useMemo(() => {
    return (
      settingRows.find(
        (r) =>
          r.settingName === EConfigurationSetting.ValueFields &&
          rowMatchesScope(r)
      ) ?? null
    );
  }, [settingRows, rowMatchesScope]);

  const thresholdScopeRow = useMemo(() => {
    return (
      settingRows.find(
        (r) =>
          r.settingName === EConfigurationSetting.ValueChangeThreshold &&
          rowMatchesScope(r)
      ) ?? null
    );
  }, [settingRows, rowMatchesScope]);

  const indexationScopeRow = useMemo(() => {
    return (
      settingRows.find(
        (r) =>
          r.settingName === EConfigurationSetting.Indexation &&
          rowMatchesScope(r)
      ) ?? null
    );
  }, [settingRows, rowMatchesScope]);

  const fetchData = useCallback(async () => {
    if (!clientId) return;
    try {
      setDataLoading(true);
      const [
        rowsResponse,
        effectiveResponse,
        effectiveThresholdsResponse,
        effectiveIndexationResponse,
      ] = await Promise.all([
        getConfigurationListSettings({
          clientId,
          settingsNames: `${EConfigurationSetting.ValueFields},${EConfigurationSetting.ValueChangeThreshold},${EConfigurationSetting.Indexation}`,
        }),
        getEffectiveValueFieldsVisibility({
          clientId,
          locationId: selectedLocationId ?? undefined,
        }),
        getEffectiveValueChangeThresholds({
          clientId,
          locationId: selectedLocationId ?? undefined,
        }),
        getEffectiveIndexation({
          clientId,
          locationId: selectedLocationId ?? undefined,
        }),
      ]);
      const rows = rowsResponse.data ?? [];
      setSettingRows(rows);
      setEffective(effectiveResponse);
      setEnabledFields(effectiveResponse?.enabledFields ?? [...TOGGLEABLE_VALUE_FIELDS]);
      setEffectiveThresholds(effectiveThresholdsResponse);
      // pre-fill with the currently effective values so a saved override is always complete
      const inputs: Record<string, string> = {};
      THRESHOLD_VALUE_FIELDS.forEach((fieldKey) => {
        inputs[fieldKey] = String(
          getEffectiveThreshold(
            effectiveThresholdsResponse,
            toFormFieldName(fieldKey)
          )
        );
      });
      setThresholdInputs(inputs);
      setEffectiveIndexation(effectiveIndexationResponse);
      setIndexationEnabled(effectiveIndexationResponse?.enabledFields ?? []);
      const idxInputs: Record<string, string> = {};
      INDEXABLE_VALUE_FIELDS.forEach((fieldKey) => {
        const percent =
          effectiveIndexationResponse?.percentages?.[fieldKey] ??
          effectiveIndexationResponse?.percentages?.[toFormFieldName(fieldKey)];
        idxInputs[fieldKey] =
          typeof percent === "number" ? String(percent) : "";
      });
      setIndexationInputs(idxInputs);
      setDataLoading(false);
    } catch (error: any) {
      toast.error(error.message);
      setDataLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, selectedLocationId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const scopeOptions: IDropdownOption[] = useMemo(() => {
    const options: IDropdownOption[] = [
      { key: CLIENT_LEVEL_KEY, text: t("rvm.valueFieldsClientLevel.label") },
    ];
    (locations ?? []).forEach((l: any) => {
      const locationId = l.locationId ?? l.location?.locationId;
      const locationName =
        l.locationName ?? l.location?.locationName ?? String(locationId);
      if (locationId) {
        options.push({ key: String(locationId), text: locationName });
      }
    });
    return options;
  }, [locations, t]);

  const handleToggleField = useCallback((fieldKey: string, checked: boolean) => {
    setEnabledFields((prev) =>
      checked ? [...prev, fieldKey] : prev.filter((f) => f !== fieldKey)
    );
  }, []);

  const handleThresholdInputChange = useCallback(
    (fieldKey: string, value: string) => {
      setThresholdInputs((prev) => ({ ...prev, [fieldKey]: value ?? "" }));
    },
    []
  );

  const thresholdsValid = useMemo(() => {
    return THRESHOLD_VALUE_FIELDS.every((fieldKey) => {
      const parsed = Number(thresholdInputs[fieldKey]);
      return Number.isFinite(parsed) && parsed > 0;
    });
  }, [thresholdInputs]);

  const handleToggleIndexation = useCallback(
    (fieldKey: string, checked: boolean) => {
      setIndexationEnabled((prev) =>
        checked ? [...prev, fieldKey] : prev.filter((f) => f !== fieldKey)
      );
    },
    []
  );

  const handleIndexationInputChange = useCallback(
    (fieldKey: string, value: string) => {
      setIndexationInputs((prev) => ({ ...prev, [fieldKey]: value ?? "" }));
    },
    []
  );

  // every enabled indexation field must carry a positive percentage
  const indexationValid = useMemo(() => {
    return indexationEnabled.every((fieldKey) => {
      const parsed = Number(indexationInputs[fieldKey]);
      return Number.isFinite(parsed) && parsed > 0;
    });
  }, [indexationEnabled, indexationInputs]);

  const handleSave = async () => {
    try {
      setDataLoading(true);
      const settingValue = JSON.stringify({ enabledFields });
      if (scopeRow) {
        await updateConfigurationSettings({
          clientId,
          configurationSettingId: scopeRow.configurationSettingId,
          data: {
            clientId: Number(clientId),
            locationId: selectedLocationId,
            settingName: EConfigurationSetting.ValueFields,
            rowVersion: scopeRow.rowVersion,
            settingValue,
          } as any,
        });
      } else {
        await setConfigurationSettings({
          clientId,
          data: {
            clientId: Number(clientId),
            locationId: selectedLocationId,
            settingName: EConfigurationSetting.ValueFields,
            settingValue,
          } as any,
        });
      }
    } catch (error: any) {
      toast.error(error.message);
      setDataLoading(false);
      return;
    }

    try {
      const thresholds: Record<string, number> = {};
      THRESHOLD_VALUE_FIELDS.forEach((fieldKey) => {
        thresholds[fieldKey] = Number(thresholdInputs[fieldKey]);
      });
      const thresholdSettingValue = JSON.stringify({ thresholds });
      if (thresholdScopeRow) {
        await updateConfigurationSettings({
          clientId,
          configurationSettingId: thresholdScopeRow.configurationSettingId,
          data: {
            clientId: Number(clientId),
            locationId: selectedLocationId,
            settingName: EConfigurationSetting.ValueChangeThreshold,
            rowVersion: thresholdScopeRow.rowVersion,
            settingValue: thresholdSettingValue,
          } as any,
        });
      } else {
        await setConfigurationSettings({
          clientId,
          data: {
            clientId: Number(clientId),
            locationId: selectedLocationId,
            settingName: EConfigurationSetting.ValueChangeThreshold,
            settingValue: thresholdSettingValue,
          } as any,
        });
      }
    } catch (error: any) {
      toast.error(error.message);
      setDataLoading(false);
      return;
    }

    try {
      // only enabled fields are persisted; an empty configuration deletes the override entirely
      if (indexationEnabled.length === 0) {
        if (indexationScopeRow) {
          await deleteConfigurationSettings({
            clientId,
            configurationSettingId: indexationScopeRow.configurationSettingId,
            rowVersion: indexationScopeRow.rowVersion,
          });
        }
      } else {
        const percentages: Record<string, number> = {};
        indexationEnabled.forEach((fieldKey) => {
          percentages[fieldKey] = Number(indexationInputs[fieldKey]);
        });
        const indexationSettingValue = JSON.stringify({
          enabledFields: indexationEnabled,
          percentages,
        });
        if (indexationScopeRow) {
          await updateConfigurationSettings({
            clientId,
            configurationSettingId: indexationScopeRow.configurationSettingId,
            data: {
              clientId: Number(clientId),
              locationId: selectedLocationId,
              settingName: EConfigurationSetting.Indexation,
              rowVersion: indexationScopeRow.rowVersion,
              settingValue: indexationSettingValue,
            } as any,
          });
        } else {
          await setConfigurationSettings({
            clientId,
            data: {
              clientId: Number(clientId),
              locationId: selectedLocationId,
              settingName: EConfigurationSetting.Indexation,
              settingValue: indexationSettingValue,
            } as any,
          });
        }
      }
      toast.success(t("rvm.success"));
      await fetchData();
    } catch (error: any) {
      toast.error(error.message);
      setDataLoading(false);
    }
  };

  const handleResetToInherited = async () => {
    if (!scopeRow && !thresholdScopeRow && !indexationScopeRow) return;
    try {
      setDataLoading(true);
      if (scopeRow) {
        await deleteConfigurationSettings({
          clientId,
          configurationSettingId: scopeRow.configurationSettingId,
          rowVersion: scopeRow.rowVersion,
        });
      }
      if (thresholdScopeRow) {
        await deleteConfigurationSettings({
          clientId,
          configurationSettingId: thresholdScopeRow.configurationSettingId,
          rowVersion: thresholdScopeRow.rowVersion,
        });
      }
      if (indexationScopeRow) {
        await deleteConfigurationSettings({
          clientId,
          configurationSettingId: indexationScopeRow.configurationSettingId,
          rowVersion: indexationScopeRow.rowVersion,
        });
      }
      toast.success(t("rvm.success"));
      await fetchData();
    } catch (error: any) {
      toast.error(error.message);
      setDataLoading(false);
    }
  };

  const inheritanceInfo = useMemo(() => {
    if (!effective) return null;
    if (scopeRow) return t("rvm.valueFieldsOverride.label");
    if (effective.isDefault) return t("rvm.valueFieldsDefault.label");
    return t("rvm.valueFieldsInherited.label");
  }, [effective, scopeRow, t]);

  const thresholdInheritanceInfo = useMemo(() => {
    if (!effectiveThresholds) return null;
    if (thresholdScopeRow) return t("rvm.valueFieldsOverride.label");
    if (effectiveThresholds.isDefault)
      return t("rvm.valueChangeThresholdsDefault.label");
    return t("rvm.valueFieldsInherited.label");
  }, [effectiveThresholds, thresholdScopeRow, t]);

  const indexationInheritanceInfo = useMemo(() => {
    if (!effectiveIndexation) return null;
    if (indexationScopeRow) return t("rvm.valueFieldsOverride.label");
    if (effectiveIndexation.isDefault)
      return t("rvm.indexationDefault.label");
    return t("rvm.valueFieldsInherited.label");
  }, [effectiveIndexation, indexationScopeRow, t]);

  return (
    <Dialog
      hidden={!showDialog}
      onDismiss={toggleShowDialog}
      dialogContentProps={{
        type: DialogType.normal,
        title: <div>{t("rvm.configValueFields.label")}</div>,
      }}
      minWidth={500}
      modalProps={{ isBlocking: true }}
    >
      <DialogContent>
        <Stack tokens={{ childrenGap: 12 }}>
          {!campaignIsClosed && (
            <MessageBar messageBarType={MessageBarType.blocked}>
              {t("rvm.valueFieldsCampaignOpen.label")}
            </MessageBar>
          )}
          <Dropdown
            label={t("rvm.valueFieldsScope.label")}
            options={scopeOptions}
            selectedKey={scopeKey}
            onChange={(_, option) => option && setScopeKey(String(option.key))}
          />
          {inheritanceInfo && (
            <MessageBar messageBarType={MessageBarType.info}>
              {inheritanceInfo}
            </MessageBar>
          )}
          {dataLoading ? (
            <Spinner size={SpinnerSize.medium} />
          ) : (
            <>
              {TOGGLEABLE_VALUE_FIELDS.map((fieldKey) => (
                <Checkbox
                  key={fieldKey}
                  label={t(`rvm.${toFormFieldName(fieldKey)}.label`)}
                  checked={enabledFields.includes(fieldKey)}
                  disabled={!campaignIsClosed}
                  onChange={(_, checked) =>
                    handleToggleField(fieldKey, !!checked)
                  }
                />
              ))}
              <Label>{t("rvm.valueChangeThresholds.label")}</Label>
              {thresholdInheritanceInfo && (
                <MessageBar messageBarType={MessageBarType.info}>
                  {thresholdInheritanceInfo}
                </MessageBar>
              )}
              {THRESHOLD_VALUE_FIELDS.map((fieldKey) => (
                <TextField
                  key={fieldKey}
                  label={t(`rvm.${toFormFieldName(fieldKey)}.label`)}
                  type="number"
                  suffix="%"
                  value={thresholdInputs[fieldKey] ?? ""}
                  disabled={!campaignIsClosed}
                  onChange={(_, value) =>
                    handleThresholdInputChange(fieldKey, value ?? "")
                  }
                />
              ))}
              {!thresholdsValid && (
                <MessageBar messageBarType={MessageBarType.error}>
                  {t("rvm.valueChangeThresholdsInvalid.label")}
                </MessageBar>
              )}
              <Label>{t("rvm.indexation.label")}</Label>
              {indexationInheritanceInfo && (
                <MessageBar messageBarType={MessageBarType.info}>
                  {indexationInheritanceInfo}
                </MessageBar>
              )}
              {INDEXABLE_VALUE_FIELDS.map((fieldKey) => (
                <Stack key={fieldKey} tokens={{ childrenGap: 4 }}>
                  <Checkbox
                    label={t(`rvm.${toFormFieldName(fieldKey)}.label`)}
                    checked={indexationEnabled.includes(fieldKey)}
                    disabled={!campaignIsClosed}
                    onChange={(_, checked) =>
                      handleToggleIndexation(fieldKey, !!checked)
                    }
                  />
                  {indexationEnabled.includes(fieldKey) && (
                    <TextField
                      label={t("rvm.indexationPercent.label")}
                      type="number"
                      suffix="%"
                      value={indexationInputs[fieldKey] ?? ""}
                      disabled={!campaignIsClosed}
                      onChange={(_, value) =>
                        handleIndexationInputChange(fieldKey, value ?? "")
                      }
                    />
                  )}
                </Stack>
              ))}
              {!indexationValid && (
                <MessageBar messageBarType={MessageBarType.error}>
                  {t("rvm.indexationInvalid.label")}
                </MessageBar>
              )}
            </>
          )}
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton
              text={t("rvm.saveConfig.label")}
              onClick={handleSave}
              disabled={
                dataLoading ||
                !campaignIsClosed ||
                !thresholdsValid ||
                !indexationValid
              }
            />
            {(scopeRow || thresholdScopeRow || indexationScopeRow) && (
              <DefaultButton
                text={t("rvm.valueFieldsResetToInherited.label")}
                onClick={handleResetToInherited}
                disabled={dataLoading || !campaignIsClosed}
              />
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default ConfigValueFieldsDialog;
